<template>
  <div class="mx-auto mxw-800">
    <v-btn
      v-if="store.state.profile"
      :ripple="false"
      :to="{ name: 'Dashboard' }"
      class="bc-body-sm px-0 mt-3"
      prepend-icon="keyboard_backspace"
      variant="text"
      tracked
    >
      {{ $bct('Back') }}
    </v-btn>

    <v-card
      v-if="contact"
      class="mt-3"
      border
      flat
      tile
    >
      <v-card-title class="fs-24 fw-500 mb-2">
        {{ $bct('Manage email preferences') }}
      </v-card-title>
      <v-card-subtitle class="fs-16">
        {{ $bct('You can opt in or out of receiving specific groups of emails.') }}
      </v-card-subtitle>
      <v-card-text>
        <v-row class="fs-14 fw-600">
          <v-col
            cols="12"
            sm="6"
          >
            {{ $bct('Email group') }}
          </v-col>
          <v-col
            class="d-flex justify-end"
            cols="12"
            sm="6"
          >
            {{ $bct('Receive emails?') }}
          </v-col>
        </v-row>
        <div v-if="role === 'manager'">
          <v-divider class="my-4" />
          <v-row>
            <v-col
              cols="12"
              sm="6"
            >
              <div class="fs-16 mb-2">
                {{ $bct('Availability reminder') }}
              </div>
              <div class="fs-14 c-light-black">
                {{ $bct('This is a weekly email reminding you to update your openings.') }}
              </div>
            </v-col>
            <v-col
              class="d-flex justify-end"
              cols="12"
              sm="6"
            >
              <v-switch
                v-model="contact.availability_reminders"
                hide-details
                mandatory
              />
            </v-col>
          </v-row>
        </div>
        <v-divider class="my-4" />
        <v-row>
          <v-col
            cols="12"
            sm="6"
          >
            <div class="fs-16 mb-2">
              {{ $bct('Messaging') }}
            </div>
            <div class="fs-14 c-light-black">
              {{
                $bct(
                  "Receive notifications when someone messages you. If you opt out, you won't be able to receive these emails.",
                )
              }}
            </div>
          </v-col>
          <v-col
            class="d-flex justify-end"
            cols="12"
            sm="6"
          >
            <v-switch
              v-model="contact.provider_inquiries"
              hide-details
              mandatory
            />
          </v-col>
        </v-row>
        <v-divider class="my-4" />
        <v-row>
          <v-col
            cols="12"
            sm="6"
          >
            <div class="fs-16 mb-2">
              {{ $bct('Unsubscribe all') }}
            </div>
            <div class="fs-14 c-light-black">
              {{
                $bct(
                  'Unsubscribe from all emails. You will continue to receive login and password reset emails when you request them.',
                )
              }}
            </div>
          </v-col>
          <v-col
            class="d-flex justify-end"
            cols="12"
            sm="6"
          >
            <v-checkbox
              v-model="contact.unsubscribe_all"
              hide-details
              mandatory
            />
          </v-col>
        </v-row>
        <v-divider class="my-4" />
        <v-row dense>
          <v-col cols="12">
            <div class="fs-16">
              {{ $bct('Confirm your email address to update preferences:') }}
            </div>
          </v-col>
          <v-col
            cols="12"
            sm="12"
          >
            <!-- eslint-disable-next-line prefer-standard-components/no-vuetify-inputs -- TODO migrate to LabeledTextfield -->
            <v-text-field
              v-model="email"
              variant="filled"
              block
              hide-details
            />
          </v-col>
        </v-row>
      </v-card-text>
      <v-card-actions>
        <v-btn
          @click="save"
          :disabled="!canSave"
          class="w-100pc"
          color="primary"
        >
          <span>{{ $bct('Save') }}</span>
        </v-btn>
      </v-card-actions>
      <v-card-text
        v-if="!canSave && email"
        class="fs-14 c-light-black pt-0"
      >
        {{ $bct('Log in or use the link from your email to save preference changes.') }}
      </v-card-text>
    </v-card>
  </div>
</template>

<script setup>
import { debounce } from 'lodash';
import { getCurrentInstance, onUnmounted } from 'vue';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';

import useApi from '@/shared/composables/useApi';
import useEventBus from '@/shared/composables/useEventBus';

const EMAIL_LOAD_DEBOUNCE_MS = 300;
const DEFAULT_CONTACT = {
  availability_reminders: true,
  provider_inquiries: true,
  unsubscribe_all: false,
};

const { api } = useApi();
const $bct = inject('translator');
const store = useStore();
const route = useRoute();
const eventBus = useEventBus();
const instance = getCurrentInstance();

const role = instance?.appContext.config.globalProperties.$role;

const contact = ref({ ...DEFAULT_CONTACT });
const email = ref(null);
const loadedEmail = ref(null);
const token = ref(null);
const tokenEmail = ref(null);

function normalizeEmail(value) {
  return value?.toString().trim().toLowerCase() || null;
}

function resetContactPreferences() {
  contact.value = { ...DEFAULT_CONTACT };
  loadedEmail.value = null;
}

function authorizationTokenFor(emailAddress) {
  if (!token.value || !tokenEmail.value) return null;
  if (normalizeEmail(emailAddress) !== normalizeEmail(tokenEmail.value)) return null;

  return token.value;
}

const ownsContactEmail = computed(() => {
  const profileEmail = normalizeEmail(store.state.profile?.email);
  const contactEmail = normalizeEmail(email.value);

  return Boolean(profileEmail && contactEmail && profileEmail === contactEmail);
});

const emailMatchesLoadedContact = computed(() => {
  const contactEmail = normalizeEmail(email.value);
  const loadedContactEmail = normalizeEmail(loadedEmail.value);

  return Boolean(contactEmail && loadedContactEmail && contactEmail === loadedContactEmail);
});

const tokenAuthorizesLoadedContact = computed(() => {
  return Boolean(authorizationTokenFor(loadedEmail.value));
});

const canSave = computed(() => {
  if (!email.value || !emailMatchesLoadedContact.value) return false;

  return ownsContactEmail.value || tokenAuthorizesLoadedContact.value;
});

function resolveSubscriptionEmail(profileEmail) {
  const queryEmail = route.query.email?.toString().trim();
  if (queryEmail) return queryEmail;

  return profileEmail || null;
}

function load() {
  if (!email.value?.toString().trim()) {
    resetContactPreferences();
    return;
  }

  const emailToLoad = email.value;
  loadedEmail.value = null;
  contact.value = { ...DEFAULT_CONTACT };

  api.contact
    .get(emailToLoad, null, authorizationTokenFor(emailToLoad))
    .then((resp) => {
      if (normalizeEmail(email.value) !== normalizeEmail(emailToLoad)) return;

      contact.value = resp.data;
      loadedEmail.value = emailToLoad;
    })
    .catch(() => {
      if (normalizeEmail(email.value) !== normalizeEmail(emailToLoad)) return;

      resetContactPreferences();
    });
}

const debouncedLoad = debounce(load, EMAIL_LOAD_DEBOUNCE_MS);

function save() {
  if (!canSave.value) return;

  api.contact.update(
    loadedEmail.value,
    contact.value,
    () => {
      eventBus.chime('Email preferences saved');
    },
    authorizationTokenFor(loadedEmail.value),
  );
}

watch(email, (nextEmail, previousEmail) => {
  if (normalizeEmail(nextEmail) === normalizeEmail(loadedEmail.value) && loadedEmail.value) {
    return;
  }

  loadedEmail.value = null;
  debouncedLoad.cancel();

  if (!nextEmail?.toString().trim()) {
    resetContactPreferences();
    return;
  }

  if (previousEmail == null) {
    load();
    return;
  }

  debouncedLoad();
});

onMounted(() => {
  token.value = route.query.token?.toString() || null;
  tokenEmail.value = token.value ? route.query.email?.toString().trim() || null : null;

  store.dispatch('identify', {
    success() {
      email.value = resolveSubscriptionEmail(store.state.profile?.email);
    },
    failure() {
      email.value = resolveSubscriptionEmail(null);
    },
  });
});

onUnmounted(() => {
  debouncedLoad.cancel();
});
</script>
